/* ==========================================================================
   Cookie Policy & Privacy Statement Page View (Multilingual - 6 Languages)
   Strictly aligned with authentic audit & GDPR specifications
   Categories: Nezbytné (LocalStorage), Analytické (GA4), Externí média (YouTube)
   ========================================================================== */

import { getLocale } from '../locales/i18n.js';
import { openCookieSettings } from '../components/CookieBanner.js';

import { cookiePolicyTranslations } from '../locales/legalTranslations.js';
import { getSvgIcon } from '../components/Icons.js';
import { exportLegalDocumentToPdf, PDF_BUTTON_LABELS } from '../utils/legalPdfExport.js';

export function renderCookiePolicyPage(onNavigate) {
  const page = document.createElement('div');
  page.className = 'sed-page-cookie-policy';
  const lang = getLocale() || 'en';
  const t = cookiePolicyTranslations[lang] || cookiePolicyTranslations.en;
  const isRtl = lang === 'ar';
  const pdfBtnLabel = PDF_BUTTON_LABELS[lang] || PDF_BUTTON_LABELS.en;

  page.innerHTML = `
    <!-- Header Hero -->
    <section class="sed-section" style="background: linear-gradient(165deg, var(--blue-light) 0%, #ffffff 70%); padding: 4.5rem 0 3rem; direction: ${isRtl ? 'rtl' : 'ltr'};">
      <div class="sed-container">
        <div class="sed-section-header" style="max-width: 860px; margin: 0 auto 2rem; text-align: center;">
          <div class="sed-eyebrow" style="color: var(--blue-primary); font-weight: 800; letter-spacing: 0.08em; font-size: 0.85rem; margin-bottom: 0.75rem;">${t.eyebrow}</div>
          <h1 class="sed-page-hero-title" style="font-size: clamp(2rem, 4vw, 2.75rem); color: var(--navy-dark); font-weight: 800; line-height: 1.2; margin-bottom: 1rem;">${t.title}</h1>
          <p class="sed-section-desc" style="font-size: 1.1rem; color: var(--text-muted); line-height: 1.6;">
            ${t.subtitle}
          </p>
        </div>
      </div>
    </section>

    <!-- Content Section -->
    <section class="sed-section" style="background-color: #ffffff; padding: 2rem 0 5rem; direction: ${isRtl ? 'rtl' : 'ltr'}; text-align: ${isRtl ? 'right' : 'left'};">
      <div class="sed-container" style="max-width: 960px;">

        <!-- Company Metadata & Download Card -->
        <div class="sed-card" style="padding: 1.75rem 2rem; margin-bottom: 2rem; background: #f8fafc; border: 1.5px solid var(--slate-line); border-radius: 12px; display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 1rem;">
          <div>
            <div style="font-weight: 800; color: var(--navy-dark); font-size: 1.25rem; font-family: var(--font-heading); margin-bottom: 0.35rem;">
              JALUD Embedded s.r.o.
            </div>
            <div style="font-size: 0.92rem; color: var(--text-dark); margin-bottom: 0.3rem;">
              <strong>Nepomucká 1355/261, 326 00 Plzeň, Czech Republic • IČO: 03056287</strong>
            </div>
            <div style="font-size: 0.88rem; color: var(--text-muted); line-height: 1.6;">
              Web: <a href="https://www.jalud.eu" target="_blank" rel="noopener noreferrer" style="color: var(--blue-primary); text-decoration: none; font-weight: 600;">www.jalud.eu</a> &bull; 
              E-mail: <span class="sed-obf-email" data-u="aW5mbw==" data-d="amFsdWQtZW1iZWRkZWQuY29t" data-t="aW5mb0BqYWx1ZC1lbWJlZGRlZC5jb20=" style="color: var(--blue-primary); text-decoration: none; font-weight: 600;"><span class="sed-obf-fallback">info [at] jalud-embedded.com</span></span>
            </div>
          </div>
          <div style="display: flex; flex-direction: row; align-items: center; gap: 0.75rem; flex-wrap: wrap;">
            <div style="font-size: 0.82rem; font-weight: 700; color: var(--blue-primary); background: #ffffff; padding: 0.45rem 0.9rem; border-radius: 6px; border: 1px solid var(--slate-line); white-space: nowrap;">
              ${t.version || 'Version 2026.1'}
            </div>
            <button class="sed-btn sed-btn-outline sed-download-pdf-btn" id="sed-download-pdf-btn" style="font-size: 0.82rem; padding: 0.45rem 0.9rem; display: inline-flex; align-items: center; gap: 6px; cursor: pointer; border-radius: 6px; background: #ffffff; color: var(--blue-primary); border-color: var(--blue-primary); font-weight: 700; transition: all 0.2s; white-space: nowrap;" onmouseover="this.style.background='var(--blue-ultra-light)'" onmouseout="this.style.background='#ffffff'">
              ${getSvgIcon('download', 14, 'var(--blue-primary)')}
              <span>${pdfBtnLabel}</span>
            </button>
          </div>
        </div>

        <div class="sed-card" style="padding: 2.5rem; line-height: 1.8; color: var(--text-dark); border: 1.5px solid var(--slate-line); border-radius: 16px; box-shadow: 0 4px 24px rgba(0,0,0,0.04); background: #ffffff;">
          
          <!-- Section 1 -->
          <h2 style="font-family: var(--font-heading); font-size: 1.35rem; color: var(--navy-dark); margin-bottom: 0.85rem; font-weight: 800; display: flex; align-items: center; gap: 10px;">
            <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">1</span>
            <span>${t.sec1Title}</span>
          </h2>
          <p style="margin-bottom: 1rem; color: #334155;">
            ${t.sec1P1}
          </p>
          <p style="margin-bottom: 2rem; color: #334155;">
            ${t.sec1P2}
          </p>

          <hr style="border: 0; border-top: 1px solid var(--slate-line); margin: 2rem 0;" />

          <!-- Section 2 -->
          <h2 style="font-family: var(--font-heading); font-size: 1.35rem; color: var(--navy-dark); margin-bottom: 1.25rem; font-weight: 800; display: flex; align-items: center; gap: 10px;">
            <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">2</span>
            <span>${t.sec2Title}</span>
          </h2>
          
          <div style="margin-bottom: 1.25rem; padding: 1.25rem 1.5rem; background: var(--blue-ultra-light, #f0f7ff); border-${isRtl ? 'right' : 'left'}: 4px solid var(--blue-primary); border-radius: 8px;">
            <strong style="color: var(--navy-dark); font-size: 1rem;">${t.catATitle}</strong>
            <p style="margin: 0.5rem 0 0; font-size: 0.92rem; line-height: 1.65; color: #334155;">
              ${t.catADesc}
            </p>
          </div>

          <div style="margin-bottom: 1.25rem; padding: 1.25rem 1.5rem; background: var(--blue-ultra-light, #f0f7ff); border-${isRtl ? 'right' : 'left'}: 4px solid var(--blue-primary); border-radius: 8px;">
            <strong style="color: var(--navy-dark); font-size: 1rem;">${t.catBTitle}</strong>
            <p style="margin: 0.5rem 0 0; font-size: 0.92rem; line-height: 1.65; color: #334155;">
              ${t.catBDesc}
            </p>
          </div>

          <div style="margin-bottom: 2rem; padding: 1.25rem 1.5rem; background: var(--blue-ultra-light, #f0f7ff); border-${isRtl ? 'right' : 'left'}: 4px solid var(--blue-primary); border-radius: 8px;">
            <strong style="color: var(--navy-dark); font-size: 1rem;">${t.catCTitle}</strong>
            <p style="margin: 0.5rem 0 0; font-size: 0.92rem; line-height: 1.65; color: #334155;">
              ${t.catCDesc}
            </p>
          </div>

          <hr style="border: 0; border-top: 1px solid var(--slate-line); margin: 2rem 0;" />

          <!-- Section 3: Verified Audit Table -->
          <h2 style="font-family: var(--font-heading); font-size: 1.35rem; color: var(--navy-dark); margin-bottom: 0.85rem; font-weight: 800; display: flex; align-items: center; gap: 10px;">
            <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">3</span>
            <span>${t.sec3Title}</span>
          </h2>
          <p style="margin-bottom: 1.25rem; color: #334155; font-size: 0.95rem;">
            ${t.sec3Intro}
          </p>

          <div style="overflow-x: auto; margin-bottom: 2.25rem; border: 1px solid var(--slate-line); border-radius: 10px;">
            <table style="width: 100%; border-collapse: collapse; font-size: 0.85rem; text-align: ${isRtl ? 'right' : 'left'};">
              <thead>
                <tr style="background-color: var(--navy-dark); color: #ffffff;">
                  <th style="padding: 10px 14px; border-bottom: 1px solid #1e293b;">${t.thName}</th>
                  <th style="padding: 10px 14px; border-bottom: 1px solid #1e293b;">${t.thProvider}</th>
                  <th style="padding: 10px 14px; border-bottom: 1px solid #1e293b;">${t.thStorageType}</th>
                  <th style="padding: 10px 14px; border-bottom: 1px solid #1e293b;">${t.thPurpose}</th>
                  <th style="padding: 10px 14px; border-bottom: 1px solid #1e293b;">${t.thCategory}</th>
                  <th style="padding: 10px 14px; border-bottom: 1px solid #1e293b; white-space: nowrap;">${t.thDuration}</th>
                </tr>
              </thead>
              <tbody>
                <!-- Item 1: LocalStorage Consent -->
                <tr style="border-bottom: 1px solid var(--slate-line); background-color: #f8fafc;">
                  <td style="padding: 10px 14px; font-weight: 700; font-family: monospace; color: var(--navy-dark);">sed_cookie_consent_v2</td>
                  <td style="padding: 10px 14px; color: #475569;">jalud.eu</td>
                  <td style="padding: 10px 14px; color: #475569;">${t.consentStorageType}</td>
                  <td style="padding: 10px 14px; color: #334155;">${t.consentPurpose}</td>
                  <td style="padding: 10px 14px;"><span style="background: rgba(53, 168, 223, 0.15); color: #0284c7; padding: 3px 8px; border-radius: 4px; font-weight: 700; font-size: 0.75rem;">${t.catBadgeNecessary}</span></td>
                  <td style="padding: 10px 14px; color: #475569;">${t.consentDuration}</td>
                </tr>

                <!-- Item 2: _ga -->
                <tr style="border-bottom: 1px solid var(--slate-line);">
                  <td style="padding: 10px 14px; font-weight: 700; font-family: monospace; color: var(--navy-dark);">_ga</td>
                  <td style="padding: 10px 14px; color: #475569;">Google Analytics (jalud.eu)</td>
                  <td style="padding: 10px 14px; color: #475569;">${t.gaCookieType}</td>
                  <td style="padding: 10px 14px; color: #334155;">${t.gaPurpose}</td>
                  <td style="padding: 10px 14px;"><span style="background: rgba(16, 185, 129, 0.15); color: #059669; padding: 3px 8px; border-radius: 4px; font-weight: 700; font-size: 0.75rem;">${t.catBadgeAnalytics}</span></td>
                  <td style="padding: 10px 14px; color: #475569;">${t.gaDuration}</td>
                </tr>

                <!-- Item 3: _ga_3YZ790ZBSX -->
                <tr style="border-bottom: 1px solid var(--slate-line); background-color: #f8fafc;">
                  <td style="padding: 10px 14px; font-weight: 700; font-family: monospace; color: var(--navy-dark);">_ga_3YZ790ZBSX</td>
                  <td style="padding: 10px 14px; color: #475569;">Google Analytics (jalud.eu)</td>
                  <td style="padding: 10px 14px; color: #475569;">${t.gaCookieType}</td>
                  <td style="padding: 10px 14px; color: #334155;">${t.ga4Purpose}</td>
                  <td style="padding: 10px 14px;"><span style="background: rgba(16, 185, 129, 0.15); color: #059669; padding: 3px 8px; border-radius: 4px; font-weight: 700; font-size: 0.75rem;">${t.catBadgeAnalytics}</span></td>
                  <td style="padding: 10px 14px; color: #475569;">${t.ga4Duration}</td>
                </tr>

                <!-- Item 4: YouTube Cookies -->
                <tr>
                  <td style="padding: 10px 14px; font-weight: 700; font-family: monospace; color: var(--navy-dark);">CONSENT, SOCS, YSC, VISITOR_INFO1_LIVE</td>
                  <td style="padding: 10px 14px; color: #475569;">YouTube (youtube-nocookie.com)</td>
                  <td style="padding: 10px 14px; color: #475569;">${t.ytCookieType}</td>
                  <td style="padding: 10px 14px; color: #334155;">${t.ytPurpose}</td>
                  <td style="padding: 10px 14px;"><span style="background: rgba(239, 68, 68, 0.15); color: #dc2626; padding: 3px 8px; border-radius: 4px; font-weight: 700; font-size: 0.75rem;">${t.catBadgeMedia}</span></td>
                  <td style="padding: 10px 14px; color: #475569;">${t.ytDuration}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <hr style="border: 0; border-top: 1px solid var(--slate-line); margin: 2rem 0;" />

          <!-- Section 4 -->
          <h2 style="font-family: var(--font-heading); font-size: 1.35rem; color: var(--navy-dark); margin-bottom: 0.85rem; font-weight: 800; display: flex; align-items: center; gap: 10px;">
            <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">4</span>
            <span>${t.sec4Title}</span>
          </h2>
          <p style="margin-bottom: 0.85rem; color: #334155;">
            ${t.sec4P1}
          </p>
          <p style="margin-bottom: 1.25rem; color: #334155;">
            ${t.sec4P2}
          </p>
          
          <div style="margin-bottom: 1.5rem;">
            <button id="sed-cookie-policy-reopen-btn" class="sed-btn" style="background-color: var(--blue-primary); color: #ffffff; padding: 0.65rem 1.4rem; border-radius: 8px; font-weight: 700; font-size: 0.92rem; border: none; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; box-shadow: 0 2px 8px rgba(53, 168, 223, 0.3);">
              <span>⚙️</span> ${t.changeSettingsBtn}
            </button>
          </div>

          <p style="margin-bottom: 2rem; color: #475569; font-size: 0.92rem;">
            ${t.sec4P3}
          </p>

          <hr style="border: 0; border-top: 1px solid var(--slate-line); margin: 2rem 0;" />

          <!-- Section 5 -->
          <h2 style="font-family: var(--font-heading); font-size: 1.35rem; color: var(--navy-dark); margin-bottom: 0.85rem; font-weight: 800; display: flex; align-items: center; gap: 10px;">
            <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">5</span>
            <span>${t.sec5Title}</span>
          </h2>
          <p style="margin-bottom: 0.5rem; color: #334155;">${t.sec5P1}</p>
          <div style="padding: 1.25rem; background: #f8fafc; border: 1px solid var(--slate-line); border-radius: 8px; margin-bottom: 1.25rem; font-size: 0.92rem; line-height: 1.65; color: #1e293b;">
            <strong>${t.companyName}</strong><br />
            ${t.companyAddress}<br />
            ${t.companyId}<br />
            <strong>${t.contactEmail}</strong>
          </div>

          <p style="margin-bottom: 1rem; color: #334155; font-size: 0.92rem;">
            ${t.privacyLinkText} <a href="#privacy-policy" id="sed-cookie-to-privacy" style="color: var(--blue-primary); font-weight: 700; text-decoration: underline;">${t.privacyPageLink}</a>.
          </p>

          <div style="padding-top: 1rem; border-top: 1px solid var(--slate-line); font-size: 0.86rem; color: #64748b;">
            <strong>${t.googleLinksIntro}</strong><br />
            • <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style="color: var(--blue-primary); text-decoration: underline;">${t.googlePrivacyLink} ↗</a><br />
            • <a href="https://policies.google.com/technologies/cookies" target="_blank" rel="noopener noreferrer" style="color: var(--blue-primary); text-decoration: underline;">${t.googleCookieLink} ↗</a>
          </div>

        </div>
      </div>
    </section>
  `;

  // Attach button click to open settings modal
  const reopenBtn = page.querySelector('#sed-cookie-policy-reopen-btn');
  if (reopenBtn) {
    reopenBtn.addEventListener('click', (e) => {
      e.preventDefault();
      openCookieSettings(onNavigate);
    });
  }

  // Attach internal privacy link
  const privacyLink = page.querySelector('#sed-cookie-to-privacy');
  if (privacyLink) {
    privacyLink.addEventListener('click', (e) => {
      e.preventDefault();
      if (typeof onNavigate === 'function') {
        onNavigate('privacy-policy');
      } else {
        window.location.hash = 'privacy-policy';
      }
    });
  }

  // Attach PDF export button handler
  const pdfBtn = page.querySelector('#sed-download-pdf-btn');
  if (pdfBtn) {
    pdfBtn.addEventListener('click', (e) => {
      e.preventDefault();
      exportLegalDocumentToPdf('cookiePolicy', lang, pdfBtn);
    });
  }

  return page;
}
